/**
 * Parts Service - Integrated with Marketplace API
 * Searches and retrieves component data from marketplace-api.partzflow.com
 */

import marketplaceService from './marketplaceService';

class PartsService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Search parts using the full-text search endpoint
   */
  async searchParts(query, options = {}) {
    const cacheKey = `search_${query}_${JSON.stringify(options)}`;
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    const raw = await marketplaceService.searchComponents(query, options.limit || 25);
    const components = Array.isArray(raw) ? raw : (raw?.items || raw?.results || []);

    // Apply available-only filter client-side (search endpoint doesn't support it natively)
    const filtered = options.inStockOnly !== false
      ? components.filter((c) => c.available_quantity > 0 && c.status === 'active')
      : components;

    const parts = filtered.map((component) => this.transformComponentToPart(component));
    const enhancedResults = await this._enrichWithImages(parts);

    this.cache.set(cacheKey, { data: enhancedResults, timestamp: Date.now() });
    return enhancedResults;
  }

  /**
   * Get part details by part number
   */
  async getPartDetails(partNumber, source = null) {
    const cacheKey = `details_${partNumber}_${source}`;
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    const raw = await marketplaceService.getComponents({
      mpn: partNumber,
      limit: 10,
    });
    const components = Array.isArray(raw) ? raw : (raw?.items || raw?.results || []);

    const parts = components.map((component) => this.transformComponentToPart(component));
    const enhancedResults = await this._enrichWithImages(parts);

    this.cache.set(cacheKey, { data: enhancedResults, timestamp: Date.now() });
    return enhancedResults;
  }

  /**
   * Get popular/featured parts with images.
   */
  async getFeaturedParts(limit = 12) {
    try {
      const raw = await marketplaceService.getComponents({
        available_only: true,
        limit,
      });
      const components = Array.isArray(raw) ? raw : (raw?.items || raw?.results || []);
      const parts = components.map((component) => this.transformComponentToPart(component));
      return this._enrichWithImages(parts);
    } catch {
      return [];
    }
  }

  /**
   * Get a specific component listing by its unique numeric ID.
   */
  async getPartById(componentId) {
    const component = await marketplaceService.getComponent(componentId);
    if (!component) return null;
    const imageUrl = await this._fetchPrimaryImageUrl(component.id);
    return this.transformComponentToPart(component, imageUrl);
  }

  /**
   * Get categories from marketplace
   */
  async getCategories() {
    try {
      const categories = await marketplaceService.getCategories();
      return categories.map((cat) => ({
        id: cat.slug,
        name: cat.name,
        count: 0,
        description: cat.description,
      }));
    } catch {
      return [];
    }
  }

  /**
   * Fetch the primary image URL for a component (returns null on any error).
   * Handles both array and paginated API responses.
   */
  async _fetchPrimaryImageUrl(componentId) {
    try {
      const raw = await marketplaceService.getComponentImages(componentId);
      const images = Array.isArray(raw) ? raw : (raw?.items || raw?.images || []);
      if (!images.length) return null;
      const primary = images.find((img) => img.is_primary) || images[0];
      return primary?.image_url || null;
    } catch {
      return null;
    }
  }

  /**
   * Enrich a list of transformed parts with image URLs fetched in parallel.
   * Skips parts that already have a photo or thumbnail from the component data.
   */
  async _enrichWithImages(parts) {
    return Promise.all(
      parts.map(async (part) => {
        if (part.thumbnailUrl || part.photoUrl) return part;
        const imageUrl = await this._fetchPrimaryImageUrl(part.componentId);
        if (!imageUrl) return part;
        return { ...part, photoUrl: imageUrl, thumbnailUrl: imageUrl };
      })
    );
  }

  /**
   * Parse the `extra` JSON column — gracefully returns {} on any failure.
   * Note: JSON.parse("null") === null, so we guard against non-object results.
   */
  _parseExtra(component) {
    if (!component.extra) return {};
    if (typeof component.extra === 'object') return component.extra ?? {};
    try {
      let parsed = JSON.parse(component.extra);
      // Handle double-encoded extra: DB stores "\"{ ... }\"" so first parse gives a string
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * Transform a raw API component row into the app's part format.
   *
   * DB schema has dedicated columns for core fields (mpn, price, quantity…)
   * and a single `extra` text column (JSON) for everything else:
   * brand, series, lead_time_weeks, photo_url, price_breaks, compliance, etc.
   * All extended fields are read from `ex` (parsed extra).
   */
  transformComponentToPart(component, imageUrl = null) {
    const ex = this._parseExtra(component);

    // Price breaks live in extra; normalize to { quantity, price }
    const priceBreaks = ex.price_breaks?.length
      ? ex.price_breaks.map((pb) => ({
          quantity: pb.break_quantity ?? pb.quantity ?? 1,
          price:    pb.unit_price    ?? pb.price    ?? component.price,
        }))
      : [{ quantity: component.minimum_order_quantity || 1, price: component.price }];

    // photo_url lives in extra; imageUrl is fetched separately as fallback
    const photoUrl     = ex.photo_url || imageUrl || null;
    const thumbnailUrl = ex.photo_url || imageUrl || null;

    return {
      // IDs
      id: `component_${component.id}`,
      componentId: component.id,

      // Part identity (core columns)
      partNumber:   component.mpn          || '',
      manufacturer: component.manufacturer || '',
      // Extended identity (extra)
      brand:             ex.brand             || component.manufacturer || '',
      series:            ex.series            || '',
      otherPartNumbers:  ex.other_part_numbers  || [],
      baseProductNumber: ex.base_product_number || '',

      // Descriptions
      description:         component.description        || '',
      detailedDescription: ex.detailed_description || '',

      // Category (core column + extra)
      category:     component.category      || '',
      categoryPath: ex.category_path || '',

      // Pricing
      unitPrice:      component.price,
      formattedPrice: this.formatPrice(component.price),
      currency:       component.currency || 'USD',
      priceBreaks,
      priceRange:     this.formatPrice(component.price),

      // Availability (core columns)
      availability:     component.available_quantity,
      availabilityText: this.formatAvailability(component.available_quantity),
      inStock:          component.available_quantity > 0 && component.status === 'active',

      // Package & physical (core column + extra)
      packaging:               component.package || '',
      package:                 component.package || '',
      packageType:             ex.package_type             || '',
      mountingType:            ex.mounting_type            || '',
      manufacturerPackageCode: ex.manufacturer_package_code || '',
      sizeDimension:           ex.size_dimension           || '',
      height:                  ex.height                   || '',
      pitch:                   ex.pitch                    || '',
      pinCount:                ex.pin_count                ?? null,

      // Inventory (extra)
      minimumOrderQuantity: component.minimum_order_quantity || 1,
      standardPackQty:      ex.standard_pack_qty  ?? null,
      maxOrderQuantity:     ex.max_order_quantity  ?? null,
      leadTimeWeeks:        ex.lead_time_weeks     ?? null,
      backOrderAllowed:     ex.back_order_allowed  ?? false,
      ncnr:                 ex.ncnr                ?? false,

      // Status & lifecycle (core column + extra)
      condition:        component.condition,
      status:           component.status,
      lifecycleStatus:  ex.lifecycle_status   || 'active',
      endOfLife:        ex.end_of_life        ?? false,
      dateLastBuyChance: ex.date_last_buy_chance || null,

      // Compliance (all in extra)
      rohsStatus:               ex.rohs_status               || null,
      reachStatus:              ex.reach_status              || null,
      moistureSensitivityLevel: ex.moisture_sensitivity_level || null,
      exportControlClass:       ex.export_control_class      || null,
      htsusCode:                ex.htsus_code               || null,
      aecQualified:             ex.aec_qualified             ?? false,
      halogenFree:              ex.halogen_free              ?? false,
      leadFree:                 ex.lead_free                 ?? false,

      // Location (core columns)
      location:    component.location     || '',
      binLocation: component.bin_location || '',

      // Links & media
      datasheet:    component.datasheet_url || '',
      datasheetUrl: component.datasheet_url || '',
      photoUrl,
      thumbnailUrl,

      // Technical specs (extra)
      parameters:     ex.parameters || [],
      specifications: this.extractSpecsFromComponent(component, ex),
      tags:           this.generateTags(component, ex),

      // Notes (core column)
      notes: component.notes || '',

      // Timestamps
      createdAt:  component.created_at,
      updatedAt:  component.updated_at,
      lastSoldAt: component.last_sold_at,

      source:   'marketplace',
      sellerId: component.seller_id ?? component.owner_id ?? component.user_id ?? null,
    };
  }

  /**
   * Build a key-value specs object from dedicated columns + extra parameters
   */
  extractSpecsFromComponent(component, ex = {}) {
    const specs = {};
    const compliance = component.compliance || {};

    if (component.mpn)               specs['MPN'] = component.mpn;
    if (component.manufacturer)      specs['Manufacturer'] = component.manufacturer;
    const series = component.series || ex.series;
    if (series)                      specs['Series'] = series;
    if (component.package)           specs['Package'] = component.package;
    const mountingType = component.mounting_type || ex.mounting_type;
    if (mountingType)                specs['Mounting Type'] = mountingType;
    const pinCount = component.pin_count ?? ex.pin_count;
    if (pinCount)                    specs['Pin Count'] = pinCount;
    const sizeDimension = component.size_dimension || ex.size_dimension;
    if (sizeDimension)               specs['Size'] = sizeDimension;
    const pitch = component.pitch || ex.pitch;
    if (pitch)                       specs['Pitch'] = pitch;
    if (component.category)          specs['Category'] = component.category;
    if (component.condition)         specs['Condition'] = component.condition;
    if (component.minimum_order_quantity) specs['Min Order Qty'] = component.minimum_order_quantity;
    const stdPack = component.standard_pack_qty ?? ex.standard_pack_qty;
    if (stdPack)                     specs['Std Pack Qty'] = stdPack;
    const leadTime = component.lead_time_weeks ?? ex.lead_time_weeks;
    if (leadTime != null)            specs['Lead Time'] = `${leadTime} week(s)`;
    const rohsStatus = compliance.rohs_status || ex.rohs_status;
    if (rohsStatus)                  specs['RoHS'] = rohsStatus;
    const msl = compliance.moisture_sensitivity_level || ex.moisture_sensitivity_level;
    if (msl)                         specs['MSL'] = msl;
    const lifecycle = component.lifecycle_status || ex.lifecycle_status;
    if (lifecycle)                   specs['Lifecycle'] = lifecycle;
    if (component.location)          specs['Location'] = component.location;

    // Append dynamic technical parameters
    const parameters = component.parameters || ex.parameters || [];
    if (Array.isArray(parameters)) {
      parameters.forEach((p) => {
        if (p.parameter_name && p.value) {
          specs[p.parameter_name] = p.value_unit ? `${p.value} ${p.value_unit}` : p.value;
        }
      });
    }

    return specs;
  }

  /**
   * Generate display tags for a component
   */
  generateTags(component, ex = {}) {
    const compliance = component.compliance || {};
    const tags = [];
    if (component.manufacturer) tags.push(component.manufacturer);
    if (component.condition)    tags.push(component.condition.charAt(0).toUpperCase() + component.condition.slice(1));
    if (component.available_quantity > 0) tags.push('In Stock');
    if (component.datasheet_url) tags.push('Datasheet');
    if (component.package)      tags.push(component.package);
    const rohsStatus = compliance.rohs_status || ex.rohs_status;
    if (rohsStatus?.toLowerCase().includes('compliant')) tags.push('RoHS');
    const aecQualified = compliance.aec_qualified ?? ex.aec_qualified;
    if (aecQualified)           tags.push('AEC-Q');
    const lifecycle = component.lifecycle_status || ex.lifecycle_status;
    if (lifecycle === 'last_time_buy') tags.push('Last Time Buy');
    if (lifecycle === 'obsolete')      tags.push('Obsolete');
    return tags;
  }

  // ────────────────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────────────────

  truncateDescription(description, maxLength) {
    if (!description) return '';
    return description.length > maxLength
      ? description.substring(0, maxLength) + '...'
      : description;
  }

  formatPrice(price) {
    if (!price || price === 0) return 'Call for price';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(price);
  }

  formatAvailability(availability) {
    if (!availability || availability === 0) return 'Out of stock';
    if (availability < 10)  return `Low stock (${availability})`;
    if (availability < 100) return `${availability} in stock`;
    return `${availability.toLocaleString()}+ in stock`;
  }

  clearCache() {
    this.cache.clear();
  }
}

export const partsService = new PartsService();

export default partsService;
